import React, { useEffect, useId, useMemo, useRef, useState } from 'react'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid'

/**
 * Lista z wyszukiwaniem (prośba właściciela 2026-10-24, A1: producent falownika i magazynu): pole, w które się pisze,
 * a pod nim podpowiedzi z listy. Bez wpisu widać wszystkie pozycje (z grupami jak w `Lista`), wpis zawęża je po
 * fragmencie nazwy bez względu na wielkość liter i polskie znaki. Klawiatura: strzałki, Enter, Escape. Po wyjściu
 * z pola wraca nazwa wybranej pozycji.
 *
 * `opcje`: [{ value, label, grupa? }], `wartosc` to `value` albo '' (wtedy `pusty` jako podpowiedź w polu).
 */
const bezZnakow = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/ł/g, 'l')

const ListaSzukana = ({ wartosc, opcje, onZmiana, etykieta, pusty = 'Wpisz albo wybierz', disabled = false, className = '' }) => {
  const id = useId()
  const wybrana = opcje.find((o) => String(o.value) === String(wartosc ?? ''))
  const [tekst, setTekst] = useState(wybrana?.label || '')
  const [otwarta, setOtwarta] = useState(false)
  const [aktywny, setAktywny] = useState(0)
  const ref = useRef(null)
  const lista = useRef(null)
  const pole = useRef(null)

  useEffect(() => { if (!otwarta) setTekst(wybrana?.label || '') }, [wybrana, otwarta])

  const fraza = bezZnakow(tekst.trim())
  const pozycje = useMemo(() => {
    if (!fraza || fraza === bezZnakow(wybrana?.label)) return opcje
    // Najpierw nazwy zaczynające się od wpisu, potem zawierające go; bez grup (lista płaska wyników).
    const zaczynaja = opcje.filter((o) => bezZnakow(o.label).startsWith(fraza))
    const zawieraja = opcje.filter((o) => !bezZnakow(o.label).startsWith(fraza) && bezZnakow(o.label).includes(fraza))
    return [...zaczynaja, ...zawieraja].map((o) => ({ ...o, grupa: undefined }))
  }, [opcje, fraza, wybrana])

  useEffect(() => {
    if (!otwarta) return undefined
    const poza = (e) => { if (ref.current && !ref.current.contains(e.target)) setOtwarta(false) }
    document.addEventListener('mousedown', poza)
    return () => document.removeEventListener('mousedown', poza)
  }, [otwarta])
  useEffect(() => { setAktywny(0) }, [fraza])
  useEffect(() => {
    if (otwarta) lista.current?.querySelector(`[data-indeks="${aktywny}"]`)?.scrollIntoView?.({ block: 'nearest' })
  }, [aktywny, otwarta])

  const wybierz = (o) => {
    setOtwarta(false)
    setTekst(o.label)
    if (String(o.value) !== String(wartosc ?? '')) onZmiana(o.value)
  }
  const klawisz = (e) => {
    if (e.key === 'Escape') { setOtwarta(false); setTekst(wybrana?.label || ''); return }
    if (e.key === 'ArrowDown') { e.preventDefault(); if (!otwarta) setOtwarta(true); else setAktywny((i) => Math.min(pozycje.length - 1, i + 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setAktywny((i) => Math.max(0, i - 1)) }
    else if (e.key === 'Enter' && otwarta && pozycje[aktywny]) { e.preventDefault(); wybierz(pozycje[aktywny]) }
  }

  return (
    <div ref={ref} className={`relative min-w-0 ${className}`}>
      <div className={`flex items-center rounded-lg border bg-white shadow-sm transition
                       ${otwarta ? 'border-blue-500 ring-2 ring-blue-100' : 'border-gray-200 hover:border-gray-300'}
                       ${disabled ? 'cursor-not-allowed bg-gray-50' : ''}`}>
        <input ref={pole} id={id} type="text" role="combobox" aria-expanded={otwarta} aria-controls={`${id}-lista`} aria-autocomplete="list"
               aria-label={etykieta} autoComplete="off" spellCheck={false} disabled={disabled} value={tekst} placeholder={pusty}
               onFocus={(e) => { setOtwarta(true); e.target.select() }}
               onChange={(e) => { setTekst(e.target.value); setOtwarta(true) }}
               onKeyDown={klawisz}
               className={`w-full min-w-0 rounded-lg border-0 bg-transparent px-3 py-2 text-sm placeholder:text-gray-500 focus:outline-none focus:ring-0
                           ${wybrana && tekst === wybrana.label ? 'font-semibold text-gray-900' : 'text-gray-900'}`} />
        <button type="button" tabIndex={-1} disabled={disabled} aria-label={etykieta}
                onClick={() => { if (otwarta) setOtwarta(false); else pole.current?.focus() }}
                className="flex-shrink-0 px-2 text-gray-400 hover:text-gray-600">
          <ChevronUpDownIcon className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
      {otwarta && (
        <ul ref={lista} id={`${id}-lista`} role="listbox" aria-label={etykieta}
            className="absolute inset-x-0 z-40 mt-1 max-h-72 overflow-y-auto overscroll-contain rounded-xl border border-gray-200 bg-white p-1 shadow-lg [scrollbar-width:thin]">
          {!pozycje.length && <li className="px-2.5 py-1.5 text-xs text-gray-500">Brak takiej pozycji.</li>}
          {pozycje.map((o, i) => {
            const nowaGrupa = o.grupa && o.grupa !== pozycje[i - 1]?.grupa
            const zaznaczona = String(o.value) === String(wartosc ?? '')
            return (
              <React.Fragment key={`${o.grupa || ''}:${o.value}`}>
                {nowaGrupa && (
                  <li role="presentation" className={`px-2.5 pb-1 text-[11px] font-semibold text-gray-500 ${i > 0 ? 'mt-1 border-t border-gray-100 pt-2' : 'pt-1'}`}>{o.grupa}</li>
                )}
                <li role="option" aria-selected={zaznaczona} data-indeks={i} onMouseEnter={() => setAktywny(i)}
                    onMouseDown={(e) => e.preventDefault()} onClick={() => wybierz(o)}
                    className={`flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-sm ${i === aktywny ? 'bg-blue-50' : ''}`}>
                  <span className={`min-w-0 flex-1 truncate ${zaznaczona ? 'font-semibold text-blue-800' : 'text-gray-800'}`}>{o.label}</span>
                  {zaznaczona && <CheckIcon className="h-4 w-4 flex-shrink-0 text-blue-600" aria-hidden="true" />}
                </li>
              </React.Fragment>
            )
          })}
        </ul>
      )}
    </div>
  )
}

export default ListaSzukana
